import { action, onConnect } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error } from '@root/shared'
import { apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'

import type { SplashPointsInfoListData } from '@blade/api-client'
import type { CurrencyModel } from '@root/entities/currency'
import type { PremiumTier } from '@root/entities/premium'

export type SplashPointsBreakdown = NonNullable<
  SplashPointsInfoListData['splashPointsBreakdown']
>
export type SplashPointsValuesBreakdown = NonNullable<
  SplashPointsInfoListData['valuesBreakdown']
>

type SplashPointsInfo = {
  currency: CurrencyModel
  tier: PremiumTier | undefined
  splashPointsAmount: number
  splashPointsBreakdown: SplashPointsBreakdown | undefined
  valuesBreakdown: SplashPointsValuesBreakdown | undefined
}

const fetchSplashPointsInfoRequestAction = action(
  async (
    ctx,
    { currency }: { currency: CurrencyModel },
  ): Promise<Result.Result<SplashPointsInfo, Error.UnknownError>> => {
    const { mainApiClient } = ctx.get(dependenciesAtom)
    const network = ctx.get(selectedNetworkAtom)

    const response = await ctx.schedule(() =>
      apiRequest(ctx, mainApiClient.trending.splashPointsInfoList, [
        {
          chain: Network.toDto(network),
          token: currency.address,
        },
      ]),
    )

    return pipe(
      response,
      Result.map((info) => ({
        ...info,
        currency,
        tier: info.tier === 'no-tier' ? undefined : (info.tier as PremiumTier),
        splashPointsBreakdown: info.splashPointsBreakdown,
        valuesBreakdown: info.valuesBreakdown,
      })),
      Result.mapError(Error.UnknownError.fallbackToUnknown),
    )
  },
  'fetchSplashPointsInfoRequestAction',
)

export const reatomSplashPointsInfo = (currency: CurrencyModel) => {
  const theAtom = reatomRequest({
    execAction: fetchSplashPointsInfoRequestAction,
    prepare: (_ctx) => ({ currency }),
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'fetchSplashPointsInfoRequestAction',
  })

  onConnect(theAtom, (ctx) => {
    theAtom.execute(ctx)
  })

  return theAtom
}
